import React, { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../../Contexts/APIContext';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { InputProps } from '../Interfaces/InputProps';
import { InputType } from '../Enums/InputType';
import QaFiltersViewModel from './ViewModels/QaFiltersViewModel';
import CallTableViewModel from '../../../ViewModels/CallTableViewModel';
import { handleAutoCompleteChange } from '../Utility/FilterChangeHandlers';
import Chip from '@mui/material/Chip';

interface IQaFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function QaFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: IQaFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [qaFilters, setQaFilters] = useState<QaFiltersViewModel>(callTableViewModel.filters.qaFilters);
    const [monitors, setMonitors] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setQaFilters(callTableViewModel.filters.qaFilters);
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('search', `GetQADropdown?client=${callTableViewModel.filters.client}`, (data) => setMonitors(data));
        }
    }, [callTableViewModel.filters.client])

    const monitoringStatusOptions = ['Not Started', 'In Progress', 'Completed'];
    const monitoringResultOptions = ['Pass', 'Fail', 'Critical Fail'];
    const monitoringResqResultOptions = ['Pass', 'Fail'];
    const alertOptions = ['Green', 'Amber', 'Red'];

    const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
    const checkedIcon = <CheckBoxIcon fontSize='small' />;

    const inputProps: InputProps[] = [
        {
            name: 'monitoringStatus',
            label: 'Monitoring Status',
            value: qaFilters.monitoringStatus,
            options: monitoringStatusOptions,
            type: InputType.AutoComplete
        },
        {
            name: 'monitoredBy',
            label: 'Monitored By',
            value: qaFilters.monitoredBy,
            options: monitors,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'monitoringResult',
            label: 'Monitoring Result',
            value: qaFilters.monitoringResult,
            options: monitoringResultOptions,
            type: InputType.AutoComplete
        },
        {
            name: 'monitoringResqResult',
            label: 'Monitoring ResQ Result',
            value: qaFilters.monitoringResqResult,
            options: monitoringResqResultOptions,
            type: InputType.AutoComplete
        },
        {
            name: 'alert',
            label: 'Alert',
            value: qaFilters.alert,
            options: alertOptions,
            type: InputType.AutoComplete
        },
    ];

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                <InputLabel>{inputProps.label}</InputLabel>
                {
                    inputProps.type === InputType.AutoCompleteMultiple &&
                    <Autocomplete
                        multiple
                        fullWidth
                        options={inputProps.options!}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option}
                        value={inputProps.value as string[]}
                        onChange={(event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setQaFilters) }}
                        limitTags={3}
                        renderOption={(props, option, { selected }) => {
                            const { ...optionProps } = props;
                            return (
                                <li {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth />
                        )}
                        renderTags={(value, getTagProps) => {
                            const numTags = value.length;
                            const limitTags = 1;
                            return (
                                <>
                                    {value.slice(0, limitTags).map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={index}
                                            label={option}
                                        />
                                    ))}

                                    {numTags > limitTags && ` +${numTags - limitTags}`}
                                </>
                            );
                        }}
                    />
                }
                {
                    inputProps.type === InputType.AutoComplete &&
                    <>
                        <Autocomplete
                            onChange={(_e: any, newValue: string) => handleAutoCompleteChange(inputProps.name, newValue, setQaFilters)}
                            options={inputProps.options!}
                            value={inputProps.value as string}
                            fullWidth
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </>
                }
            </Grid>)
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.qaFilters = qaFilters;

        setCallTableViewModel({
            ...newCallTableViewModel,
        })

        setOpen(false);
    }

    return (
        <Dialog onClose={() => setOpen(false)} open={open} fullWidth maxWidth='sm'>
            <DialogTitle>QA Filters</DialogTitle>
            <DialogContent>
                <Grid container height='100%'>
                    {
                        inputProps.map(props => getInput(props))
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}